// ─────────────────────────────────────────────────────────────────────────────
// Andamiaje visual del Excel Demográfico (`04 - Demografico.xlsx`, plantilla de referencia del
// cliente medida celda a celda el 2026-09-04).
//
// Las 5 hojas del libro comparten la MISMA estructura de página: banner (título en navy + subtítulo
// en azul claro), dos filas en blanco, la tabla, una fila en blanco y una nota al pie en crema. Antes
// cada hoja se armaba suelta en `DemograficoExportButton.tsx` y sólo el "Resumen Ejecutivo" tenía
// banner: las otras cuatro arrancaban directas en el encabezado de tabla, sin título, con el ancho de
// columna por omisión de Excel (8.43) para nombres de Fundamental de 39 caracteres — de ahí el
// amontonamiento que se reportó sobre el archivo descargado.
//
// Este módulo centraliza esa estructura para que cada hoja sólo declare su tabla y su estilo por
// columna, y para que los índices de fila (dónde va el encabezado, dónde arranca el dato) se
// calculen en un solo lugar en vez de repetirse como literales en cada builder.
//
// 🔴 Los colores de Fundamental NO viven aquí: los sigue dando el catálogo vía `excelTintStyle`,
// igual que en pantalla. La plantilla del cliente fija la identidad de la PÁGINA (navy/crema), no la
// de los Fundamentales.
// ─────────────────────────────────────────────────────────────────────────────
import { excelColoresDemografico } from '@/utils/excelTheme';
import { mergeDeFila } from '@/utils/exportExcelMultiSheet';
import type {
  ExcelCellFillHint, ExcelCellStyleHint, ExcelCellValue, ExcelColumnStyleHint, ExcelSheetSpec,
  ExcelSheetTheme,
} from '@/utils/exportExcelMultiSheet';

/** Paleta de página de las 5 hojas — ver `excelColoresDemografico`. */
export const TEMA_DEMOGRAFICO: ExcelSheetTheme = {
  tituloFondo: excelColoresDemografico.tituloFondo,
  tituloTexto: excelColoresDemografico.tituloTexto,
  subtituloFondo: excelColoresDemografico.subtituloFondo,
  subtituloTexto: excelColoresDemografico.subtituloTexto,
  headerTablaFondo: excelColoresDemografico.headerTablaFondo,
  headerTablaTexto: excelColoresDemografico.headerTablaTexto,
  filaPar: excelColoresDemografico.filaPar,
  filaImpar: excelColoresDemografico.filaImpar,
  headerFontSize: 11,
  isHeaderWrapped: true,
  isSubtituloItalic: true,
};

/** Filas (0-indexed) del banner que abre toda hoja del libro. */
export const FILA_TITULO = 0;
export const FILA_SUBTITULO = 1;

/**
 * Fila (0-indexed) del encabezado de tabla en las hojas SIN bloque de metadatos (Definiciones,
 * Brecha, Heatmap, Red): la plantilla deja las filas 2 y 3 en blanco entre el banner y la tabla.
 */
export const FILA_HEADER_TABLA = 4;

/**
 * Alturas de fila medidas de la plantilla, en puntos. Se fijan SOLO en las filas COMBINADAS (banner
 * y nota al pie): Excel no autoajusta el alto de una fila con celdas combinadas, así que sin `ht`
 * ahí el texto se corta.
 *
 * 🔴 El encabezado de tabla NO lleva alto fijo, aunque la plantilla le ponga 28.8: sus celdas no
 * están combinadas y llevan «Ajustar texto», así que Excel calcula el alto que el rótulo necesita en
 * el idioma que toque. Un alto fijo cortaría los nombres de Fundamental del alemán o el neerlandés
 * — misma razón por la que el Excel de Líderes tampoco lo fija (D-043).
 */
const ALTO_TITULO = 32.25;
const ALTO_SUBTITULO = 27.75;
const ALTO_NOTA = 26;

/** Ancho de columna: mínimo, máximo y divisor de la longitud del encabezado — ver `anchoParaEncabezado`. */
const ANCHO_MINIMO = 14;
const ANCHO_MAXIMO = 32;
const CARACTERES_POR_UNIDAD_DE_ANCHO = 1.5;
const HOLGURA_DE_ANCHO = 6;
/** Ver `anchoParaTextoDeCelda`: una columna sin ajuste de texto necesita el largo completo. */
const ANCHO_MAXIMO_TEXTO = 48;
const HOLGURA_DE_TEXTO = 3;

/**
 * Ancho de una columna a partir del largo de su encabezado, contando con que el encabezado ajusta
 * texto en dos renglones. Reproduce los anchos medidos de la plantilla dentro de ±2 unidades
 * ("Seguridad Psicológica" → 20 vs 20.0 de la plantilla; "Impulsar el Éxito de los Demás" → 26 vs
 * 24.7; "Hacerme Cargo de mi Talento y Evolución" → 32 vs 30.4), sin fijarlos como literales: los
 * Fundamentales son configurables por tenant y se traducen, así que sus nombres no son constantes.
 */
export function anchoParaEncabezado(encabezado: string): number {
  const calculado = encabezado.length / CARACTERES_POR_UNIDAD_DE_ANCHO + HOLGURA_DE_ANCHO;
  return Math.round(Math.min(ANCHO_MAXIMO, Math.max(ANCHO_MINIMO, calculado)) * 100) / 100;
}

/** Una hoja del libro Demográfico, antes de traducirse a `ExcelSheetSpec`. */
export interface HojaDemografica {
  /** Nombre de la pestaña. */
  nombre: string;
  titulo: string;
  subtitulo: string;
  /**
   * Bloque "Periodo / Filtros aplicados / Generado" que la plantilla pone entre el banner y la
   * tabla, sólo en el "Resumen Ejecutivo". Ausente en las demás hojas.
   */
  metadatos?: ReadonlyArray<{ etiqueta: string; valor: string }>;
  /** Encabezado (fila 0) y filas de datos de la tabla. */
  tabla: ExcelCellValue[][];
  /** Nota al pie, en la banda crema que cierra la hoja. */
  nota: string;
  columnWidths: Array<number | undefined>;
  /** Estilo por columna de las filas de datos: alineación, negritas y formato numérico. */
  columnas?: ExcelColumnStyleHint[];
  /** Fills por celda, con `row` RELATIVO a la tabla (0 = encabezado) — el desplazamiento a la fila
   *  real de la hoja lo hace `construirHojaDemografica`. */
  cellFills?: ExcelCellFillHint[];
  /** Estilos por celda, con `row` relativo a la tabla igual que `cellFills`. */
  cellStyles?: ExcelCellStyleHint[];
  /** Formato numérico por columna completa (`0.0` en las de Brecha Generacional). */
  numberFormats?: Array<{ column: number; format: string }>;
  /** Filas al pie de la tabla que son un TOTAL y no un dato nuevo (la fila «Total» del Heatmap).
   *  Se descuentan del `rowCount` que se audita: la bitácora registra cuánto contenido real salió. */
  filasDeTotales?: number;
}

/** Fila (0-indexed) del encabezado de tabla de una hoja, con o sin bloque de metadatos. */
export function filaDelHeader(cantidadDeMetadatos: number): number {
  return cantidadDeMetadatos > 0
    ? FILA_SUBTITULO + 2 + cantidadDeMetadatos + 1
    : FILA_HEADER_TABLA;
}

/**
 * Arma el `ExcelSheetSpec` de una hoja del libro: banner, bloque de metadatos si lo hay, tabla y
 * nota al pie, con los merges, alturas y bordes de la plantilla ya resueltos contra el ancho real de
 * la tabla (que es dinámico: una columna por OU con censo, una por Fundamental del catálogo).
 */
export function construirHojaDemografica(hoja: HojaDemografica): ExcelSheetSpec {
  const metadatos = hoja.metadatos ?? [];
  const filaHeader = filaDelHeader(metadatos.length);

  const rows: ExcelCellValue[][] = [
    [hoja.titulo],
    [hoja.subtitulo],
    [],
    ...metadatos.map(({ etiqueta, valor }): ExcelCellValue[] => [etiqueta, valor]),
    [],
    ...hoja.tabla,
    [],
    [hoja.nota],
  ];

  const ultimaColumna = Math.max(0, hoja.columnWidths.length - 1);
  const filaUltimoDato = filaHeader + hoja.tabla.length - 1;
  const filaNota = filaUltimoDato + 2;
  const primeraFilaMetadatos = FILA_SUBTITULO + 2;

  const merges = [
    mergeDeFila(FILA_TITULO, 0, ultimaColumna),
    mergeDeFila(FILA_SUBTITULO, 0, ultimaColumna),
    mergeDeFila(filaNota, 0, ultimaColumna),
    // El valor de cada metadato se combina hasta el final de la tabla: una lista de OUs filtradas no
    // cabe en una columna y, sin combinar, Excel la corta en la primera celda ocupada a su derecha.
    ...metadatos.map((_, indice) => mergeDeFila(primeraFilaMetadatos + indice, 1, ultimaColumna)),
  ];

  const estilosDeMetadatos = metadatos.flatMap((_, indice): ExcelCellStyleHint[] => [
    {
      row: primeraFilaMetadatos + indice,
      column: 0,
      fillArgb: excelColoresDemografico.metaEtiquetaFondo,
      fontColorArgb: excelColoresDemografico.metaEtiquetaTexto,
      isBold: true,
      vertical: 'middle',
    },
    {
      row: primeraFilaMetadatos + indice,
      column: 1,
      fillArgb: excelColoresDemografico.metaValorFondo,
      fontColorArgb: excelColoresDemografico.metaValorTexto,
      vertical: 'middle',
      hasWrapText: true,
    },
  ]);

  const bordesDeMetadatos = metadatos.length > 0
    ? [{
      startRow: primeraFilaMetadatos,
      endRow: primeraFilaMetadatos + metadatos.length - 1,
      startColumn: 0,
      endColumn: ultimaColumna,
      colorArgb: excelColoresDemografico.bordeTabla,
    }]
    : [];

  return {
    name: hoja.nombre,
    rows,
    styleHints: {
      theme: TEMA_DEMOGRAFICO,
      headerRows: [filaHeader],
      merges,
      numberFormats: hoja.numberFormats,
      columnWidths: hoja.columnWidths,
      rowHeights: [
        { row: FILA_TITULO, height: ALTO_TITULO },
        { row: FILA_SUBTITULO, height: ALTO_SUBTITULO },
        { row: filaNota, height: ALTO_NOTA },
      ],
      dataColumnStyles: hoja.columnas?.length && hoja.tabla.length > 1
        ? { startRow: filaHeader + 1, endRow: filaUltimoDato, columns: hoja.columnas }
        : undefined,
      cellFills: hoja.cellFills?.map((hint) => ({ ...hint, row: hint.row + filaHeader })),
      cellStyles: [
        ...estilosDeMetadatos,
        ...(hoja.cellStyles ?? []).map((hint) => ({ ...hint, row: hint.row + filaHeader })),
      ],
      bandRows: [{
        row: filaNota,
        fillArgb: excelColoresDemografico.notaFondo,
        columnCount: hoja.columnWidths.length,
        fontColorArgb: excelColoresDemografico.notaTexto,
        isItalic: true,
        fontSize: 9,
      }],
      borderedRanges: [
        {
          startRow: filaHeader,
          endRow: filaUltimoDato,
          startColumn: 0,
          endColumn: ultimaColumna,
          colorArgb: excelColoresDemografico.bordeTabla,
        },
        {
          startRow: filaNota,
          endRow: filaNota,
          startColumn: 0,
          endColumn: ultimaColumna,
          colorArgb: excelColoresDemografico.bordeNota,
        },
        ...bordesDeMetadatos,
      ],
    },
  };
}

/**
 * Estilo de la primera columna de toda tabla del libro: la etiqueta de la fila (Generación, OU,
 * Flujo) va en navy y negrita, alineada a la izquierda.
 */
export function columnaEtiqueta(column = 0): ExcelColumnStyleHint {
  return {
    column,
    fontColorArgb: excelColoresDemografico.etiquetaFila,
    isBold: true,
    horizontal: 'left',
    vertical: 'middle',
  };
}

/**
 * Estilo de una columna numérica: centrada, con el formato que se le pase.
 *
 * 🔴 Centrada y no a la derecha a propósito: las celdas tapadas por el umbral de anonimato llevan el
 * guion largo (texto, que Excel alinea a la IZQUIERDA) y las de dato llevan un número (que alinea a
 * la DERECHA). Sin fijar la alineación, la misma columna salía con datos pegados a lados opuestos —
 * el desalineado que se reportó sobre el archivo descargado.
 */
export function columnaNumerica(column: number, numFmt = '#,##0'): ExcelColumnStyleHint {
  return {
    column,
    horizontal: 'center',
    vertical: 'middle',
    fontColorArgb: excelColoresDemografico.textoCuerpo,
    numFmt,
  };
}

/** Estilo de una columna de texto que no es la etiqueta de fila (Rango de años, Nota). */
export function columnaTexto(column: number): ExcelColumnStyleHint {
  return {
    column,
    horizontal: 'left',
    vertical: 'middle',
    fontColorArgb: excelColoresDemografico.textoCuerpo,
  };
}

/**
 * Estilo de una columna de texto largo (las tres del glosario de "Definiciones"): ajusta el texto y
 * lo alinea arriba, para que una definición de dos renglones no quede centrada verticalmente contra
 * el nombre del dato.
 */
export function columnaParrafo(column: number): ExcelColumnStyleHint {
  return {
    column,
    horizontal: 'left',
    vertical: 'top',
    hasWrapText: true,
    fontColorArgb: excelColoresDemografico.textoCuerpo,
  };
}

/** Anchos de una tabla derivados del largo de sus encabezados — ver `anchoParaEncabezado`. */
export function anchosParaEncabezados(encabezados: readonly string[]): number[] {
  return encabezados.map(anchoParaEncabezado);
}

/**
 * Ancho para una columna cuyo contenido NO ajusta texto (la de flujos «OU origen → OU destino», que
 * es bastante más larga que su encabezado). A diferencia de `anchoParaEncabezado`, que reparte el
 * texto en dos renglones, aquí el ancho tiene que dar para el texto completo en una sola línea.
 */
export function anchoParaTextoDeCelda(texto: string): number {
  return Math.min(ANCHO_MAXIMO_TEXTO, Math.max(ANCHO_MINIMO, texto.length + HOLGURA_DE_TEXTO));
}

/**
 * Estilo de una columna de totales (la «Total» del Heatmap, la «Δ Brecha»): crema azulado con el
 * número en navy, para separarla visualmente de las columnas tintadas por Fundamental.
 */
export function columnaTotal(column: number, numFmt = '#,##0'): ExcelColumnStyleHint {
  return {
    column,
    fillArgb: excelColoresDemografico.totalFilaFondo,
    fontColorArgb: excelColoresDemografico.etiquetaFila,
    isBold: true,
    horizontal: 'center',
    vertical: 'middle',
    numFmt,
  };
}
